import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

const LINE_Y_TOLERANCE = 3;
const GAP_AS_TAB = 12;

/**
 * @param {import("pdfjs-dist").TextItem[]} items
 * @returns {string[]}
 */
export function textItemsToLines(items) {
    const parsed = items
        .map((item) => {
            const text = (item.str || "").trim();
            if (!text) return null;
            const x = item.transform[4];
            const y = item.transform[5];
            const width =
                item.width > 0 ? item.width : text.length * (Math.abs(item.transform[0]) || 5) * 0.5;
            return { text, x, y, endX: x + width };
        })
        .filter(Boolean);

    if (!parsed.length) return [];

    parsed.sort((a, b) => {
        if (Math.abs(a.y - b.y) > LINE_Y_TOLERANCE) return b.y - a.y;
        return a.x - b.x;
    });

    const lines = [];
    let row = [];
    let lastY = null;

    const flush = () => {
        if (!row.length) return;
        row.sort((a, b) => a.x - b.x);
        let line = "";
        let lastEnd = -999;
        for (const item of row) {
            if (lastEnd > -900 && item.x - lastEnd > GAP_AS_TAB) {
                line += "\t";
            } else if (line) {
                line += " ";
            }
            line += item.text;
            lastEnd = item.endX;
        }
        const trimmed = line.trim();
        if (trimmed) lines.push(trimmed);
        row = [];
    };

    for (const item of parsed) {
        if (lastY !== null && Math.abs(item.y - lastY) > LINE_Y_TOLERANCE) {
            flush();
        }
        row.push(item);
        lastY = item.y;
    }
    flush();

    return lines;
}

/**
 * @param {import("pdfjs-dist").PDFDocumentProxy} pdf
 * @param {(pct: number) => void} [onProgress]
 * @returns {Promise<{ lines: string[], pageCount: number }>}
 */
export async function extractPdfLinesFromDocument(pdf, onProgress = () => {}) {
    const totalPages = pdf.numPages;
    const allLines = [];

    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent({
            disableCombineTextItems: true,
        });
        allLines.push(...textItemsToLines(textContent.items));
        onProgress(Math.round((pageNum / totalPages) * 100));
    }

    return { lines: allLines, pageCount: totalPages };
}

/**
 * @param {File} file
 * @param {(pct: number) => void} [onProgress]
 * @returns {Promise<{ lines: string[], pageCount: number, pdf: import("pdfjs-dist").PDFDocumentProxy }>}
 */
export async function loadPdfAndExtractLines(file, onProgress = () => {}) {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const { lines, pageCount } = await extractPdfLinesFromDocument(pdf, onProgress);
    return { lines, pageCount, pdf };
}

/**
 * @param {File} file
 * @param {(pct: number) => void} [onProgress]
 * @returns {Promise<{ lines: string[], pageCount: number }>}
 */
export async function extractPdfLines(file, onProgress = () => {}) {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    return extractPdfLinesFromDocument(pdf, onProgress);
}

/**
 * @param {File} file
 * @param {(pct: number) => void} [onProgress]
 * @returns {Promise<string>}
 */
export async function extractPdfPlainText(file, onProgress = () => {}) {
    const { lines } = await extractPdfLines(file, onProgress);
    return lines.join("\n");
}

/**
 * OCR fallback for image-only PDFs (e.g. exported via screenshot/canvas).
 * @param {File} file
 * @param {(pct: number) => void} [onProgress]
 */
export async function extractPdfLinesViaOcr(file, onProgress = () => {}) {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const totalPages = pdf.numPages;
    const allLines = [];

    const { createWorker } = await import("tesseract.js");
    const worker = await createWorker("eng", 1, {
        logger: (m) => {
            if (m.status === "recognizing text" && m.progress != null) {
                const pageFrac = (m.progress || 0) / totalPages;
                onProgress(Math.min(99, Math.round(pageFrac * 100)));
            }
        },
    });

    try {
        for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const viewport = page.getViewport({ scale: 2 });
            const canvas = document.createElement("canvas");
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            const ctx = canvas.getContext("2d");
            if (!ctx) continue;

            await page.render({ canvasContext: ctx, viewport, canvas }).promise;

            const {
                data: { text },
            } = await worker.recognize(canvas);
            const pageLines = String(text || "")
                .split(/\n/)
                .map((l) => l.trim())
                .filter(Boolean);
            allLines.push(...pageLines);

            onProgress(Math.round((pageNum / totalPages) * 100));
        }
    } finally {
        await worker.terminate();
    }

    return { lines: allLines, pageCount: totalPages, usedOcr: true };
}
